// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  START_STEP,
  StepDetails,
  StepsDefinition,
  getSteps,
} from "./TestRunConstants";

import { CustomerContext } from "components/App/DashboardApp";
import transcribeTestPath from "./transcribeTestPath";
import { useContext } from "react";

interface testRunStepAPI {
  getTestRunSteps: () => StepsDefinition;

  getTestRunStep: (stepId: string) => StepDetails;

  getNextStepOfType: (param: {
    currentStepName: string;
    nextStepType: string;
  }) => string | undefined;

  getNextDefaultStep: (param: {
    currentStepName: string;
  }) => string | undefined;

  getPreviousStep: (param: { currentStepName: string }) => string | undefined;

  getStartStep(): string;
}

const cachedStepsForCustomers = {};
const stepsApiForCustomer = {};

export const useTestRunConfig = (): testRunStepAPI => {
  const customContext = useContext(CustomerContext);
  if (!customContext) {
    throw new Error("Cannot access customer context");
  }
  const customContextName = customContext.name;

  if (!stepsApiForCustomer[customContextName]) {
    const api: testRunStepAPI = {
      getStartStep: () => {
        return customContext.startStep || START_STEP;
      },

      getTestRunSteps: () => {
        if (!cachedStepsForCustomers[customContextName]) {
          const steps = customContext.getTestRunDefinition
            ? customContext.getTestRunDefinition()
            : getSteps();
          cachedStepsForCustomers[customContextName] = transcribeTestPath(
            steps
          );
        }
        return cachedStepsForCustomers[customContextName];
      },

      getTestRunStep: (stepId: string): StepDetails => {
        return api.getTestRunSteps()[stepId];
      },

      getNextStepOfType: (param: {
        currentStepName: string;
        nextStepType: string;
      }): string | undefined => {
        const nextNav = api.getTestRunStep(param.currentStepName).nav.next;
        if (nextNav) {
          return nextNav[param.nextStepType];
        }
        return undefined;
      },

      getNextDefaultStep: (param: {
        currentStepName: string;
      }): string | undefined => {
        return api.getNextStepOfType({
          ...param,
          nextStepType: "default",
        });
      },

      getPreviousStep: (param: {
        currentStepName: string;
      }): string | undefined => {
        return api.getTestRunStep(param.currentStepName).nav.previous;
      },
    };
    stepsApiForCustomer[customContextName] = api;
  }

  return stepsApiForCustomer[customContextName];
};
